import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Chip,
  Card,
  CardContent,
  Stack,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { apiService } from '@/services/api';
import { ClientCell } from '@/components/ClientCell';
import { ProductCell } from '@/components/ProductCell';
import { useFilters } from '@/contexts/FilterContext';
import { useRefresh } from '@/contexts/RefreshContext';
import { useConfirmation } from '@/contexts/ConfirmationContext';
import { useMessage } from '@/contexts/MessageContext';
import { useProductsMap } from '@/hooks/useProductsMap';
import { logger } from '@/utils/logger';
import { ProductScopeGate } from '@/components/ProductScopeGate';

export const ImpersonationPage: React.FC = () => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const { selectedClients, productId } = useFilters();
  const { setRefreshCallback } = useRefresh();
  const { confirm } = useConfirmation();
  const { showSuccess, showError } = useMessage();
  const productsById = useProductsMap();
  const [statusTab, setStatusTab] = useState<'all' | 'active' | 'closed'>('all');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (productId?.trim()) params.productId = productId.trim();

      // Aplicar filtro de clientes se houver seleção
      if (selectedClients.length > 0) {
        params.impersonatedTenantId =
          selectedClients.length === 1 ? selectedClients[0] : selectedClients;
      }

      const response = await apiService.getSessions(params);

      if (response.data.success) {
        setSessions(response.data.data);
        setTotal(response.data.pagination.total);
      }
    } catch (error) {
      setSessions([]);
      setTotal(0);
      setLoadError(error);
      logger.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedClients, productId]);

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setPage(0);
  }, [selectedClients, productId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Registrar callback de refresh
  useEffect(() => {
    setRefreshCallback(loadSessions);
    return () => setRefreshCallback(null);
  }, [loadSessions, setRefreshCallback]);

  const filteredSessions = sessions.filter(session => {
    if (statusTab === 'active') return session.isActive;
    if (statusTab === 'closed') return !session.isActive;
    return true;
  });

  const handleTerminate = (session: any) => {
    confirm({
      title: 'Encerrar sessão de personificação',
      message: `Encerrar a sessão de ${session.impersonatorEmail || 'operador'} em ${session.tenantName || session.impersonatedTenantId || 'cliente'}? O acesso personificado será interrompido imediatamente.`,
      confirmText: 'Encerrar sessão',
      cancelText: 'Cancelar',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await apiService.terminateSession(session._id);
          showSuccess('Sessão encerrada.');
          loadSessions();
        } catch (error) {
          logger.error('Error terminating session:', error);
          showError('Erro ao encerrar sessão.');
        }
      }
    });
  };

  const scopedProductId = productId?.trim() || undefined;

  return (
    <ProductScopeGate
      productId={scopedProductId}
      requireSelection={false}
      integration={scopedProductId ? 'appApi' : 'core'}
      loading={loading}
      loadError={loadError}
      isEmpty={false}
      onRetry={loadSessions}
    >
      <Box>
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'action.hover' }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="h4">Sessões de personificação</Typography>
              <Typography variant="body2" color="text.secondary">
                Acompanhe acessos administrativos feitos em nome de usuários finais. Esta tela é de
                auditoria operacional: mostra quem iniciou a sessão, qual produto/cliente foi
                acessado e permite encerrar sessões ativas.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <ProductCell
                  productId={productId}
                  name={productsById.get(productId)?.displayName}
                  avatarUrl={productsById.get(productId)?.avatarUrl}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    selectedClients.length
                      ? `${selectedClients.length} cliente(s)`
                      : 'Todos os clientes'
                  }
                />
                <Chip size="small" variant="outlined" label="Ação crítica: encerrar sessão" />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Alert severity="info" sx={{ mb: 2 }}>
          Sessões ativas devem ser encerradas quando o atendimento terminar. Todos os eventos devem
          permanecer rastreáveis no audit log.
        </Alert>

        <Paper sx={{ mb: 2 }}>
          <Tabs value={statusTab} onChange={(_, v) => setStatusTab(v)} variant="scrollable">
            <Tab value="all" label={`Todas (${sessions.length})`} />
            <Tab value="active" label={`Ativas (${sessions.filter(s => s.isActive).length})`} />
            <Tab
              value="closed"
              label={`Encerradas (${sessions.filter(s => !s.isActive).length})`}
            />
          </Tabs>
        </Paper>

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Produto</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Impersonador</TableCell>
                <TableCell>Impersonado</TableCell>
                <TableCell>Iniciada em</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filteredSessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nenhuma sessão encontrada para o filtro atual.
                  </TableCell>
                </TableRow>
              ) : (
                filteredSessions.map(session => (
                  <TableRow key={session._id}>
                    <TableCell>
                      {session.productId ? (
                        <ProductCell
                          productId={session.productId}
                          name={productsById.get(session.productId)?.displayName}
                          avatarUrl={productsById.get(session.productId)?.avatarUrl}
                        />
                      ) : (
                        <ProductCell
                          productId={productId}
                          name={productsById.get(productId)?.displayName}
                          avatarUrl={productsById.get(productId)?.avatarUrl}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <ClientCell
                        name={session.tenantName}
                        tenantId={session.impersonatedTenantId}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{session.impersonatorEmail}</TableCell>
                    <TableCell>{session.impersonatedEmail}</TableCell>
                    <TableCell>{new Date(session.startedAt).toLocaleString('pt-BR')}</TableCell>
                    <TableCell>
                      <Chip
                        label={session.isActive ? 'Ativa' : 'Encerrada'}
                        color={session.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {session.isActive && (
                        <Button size="small" color="error" onClick={() => handleTerminate(session)}>
                          Encerrar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Box>
    </ProductScopeGate>
  );
};

export default ImpersonationPage;
